import { ChevronDownIcon, ChevronUpIcon } from '@/components/atoms/icons';
import {
  Typography,
  type TypographyVariant,
} from '@/components/atoms/Typography';
import { useUIColors } from '@/core/hooks';
import { useI18nContext } from '@/core/providers/I18nProvider';
import { isSmallScreen } from '@/core/utils/responsive';
import React, { useState } from 'react';
import {
  StyleSheet,
  TouchableOpacity,
  View,
  type ViewStyle,
} from 'react-native';

/**
 * Web catalogue accordion variants (properties-web + guest-web).
 * - large: About Unit (bigger title, roomier content) — tested unit-details default
 * - compact: Documents (tighter header + content)
 * On small screens both variants fall back to the mobile spacing/title.
 *
 * Does NOT replace the mobile-owned shared `Accordion` molecule.
 *
 * Default layout (no padding/title overrides) must stay pixel-identical to the
 * former properties-web `UnitAccordion` implementation.
 */
export type WebAccordionVariant = 'large' | 'compact';

export interface WebAccordionProps {
  icon: React.ComponentType<{ size?: number; color?: string }>;
  title: string;
  /** Optional second line under the title (footnote). Guest/docs only when needed. */
  subtitle?: string;
  children: React.ReactNode;
  variant?: WebAccordionVariant;
  defaultExpanded?: boolean;
  onToggle?: (isExpanded: boolean) => void;
  /** Override title Typography variant (defaults from variant + screen size). */
  titleVariant?: TypographyVariant;
  /** Override header horizontal padding. */
  headerPaddingHorizontal?: number;
  /** Override header vertical padding. */
  headerPaddingVertical?: number;
  /** Override header bottom padding only (no top). */
  headerPaddingBottom?: number;
  /** Override content horizontal padding. */
  contentPaddingHorizontal?: number;
  /** Override content top padding. */
  contentPaddingTop?: number;
  /** Override content bottom padding. */
  contentPaddingBottom?: number;
  /**
   * Gap between header icon, title, and chevron.
   * Defaults to 12 (original UnitAccordion / unit-details).
   */
  headerGap?: number;
  /**
   * Uniform padding on the whole accordion frame (Figma auto-layout padding).
   * When set, variant header/content paddings are zeroed so inset comes only
   * from this value — unit-details must NOT pass this.
   */
  padding?: number;
  /** Override only the frame’s top padding (e.g. 0 = no top gap). */
  paddingTop?: number;
  /**
   * Gap between header row and content body when `padding` is used.
   * Defaults to 16 (Figma frame gap). Ignored when `padding` is unset.
   */
  sectionGap?: number;
}

interface ResolvedLayout {
  headerStyle: ViewStyle;
  contentStyle: ViewStyle;
  titleVariant: 'headline' | 'title3' | 'headlineLarge';
}

/**
 * Same resolution rules as the original UnitAccordion (byte-for-byte defaults).
 */
function resolveLayout(
  variant: WebAccordionVariant,
  isMobile: boolean,
): ResolvedLayout {
  const isLarge = !isMobile && variant === 'large';

  let headerStyle: ViewStyle = styles.headerCompact;
  if (isMobile) {
    headerStyle = styles.headerMobile;
  } else if (isLarge) {
    headerStyle = styles.headerLarge;
  }

  let contentStyle: ViewStyle = styles.contentCompact;
  if (isMobile) {
    contentStyle = styles.contentMobile;
  } else if (isLarge) {
    contentStyle = styles.contentLarge;
  }

  let titleVariant: 'headline' | 'title3' | 'headlineLarge' = 'headlineLarge';
  if (isMobile) {
    titleVariant = 'headline';
  } else if (isLarge) {
    titleVariant = 'title3';
  }

  return { headerStyle, contentStyle, titleVariant };
}

function buildPadOverride(options: {
  paddingHorizontal?: number;
  paddingVertical?: number;
  paddingTop?: number;
  paddingBottom?: number;
}): ViewStyle | undefined {
  const style: ViewStyle = {};
  let hasOverride = false;
  if (options.paddingHorizontal !== undefined) {
    style.paddingHorizontal = options.paddingHorizontal;
    hasOverride = true;
  }
  if (options.paddingVertical !== undefined) {
    style.paddingVertical = options.paddingVertical;
    hasOverride = true;
  }
  if (options.paddingTop !== undefined) {
    style.paddingTop = options.paddingTop;
    hasOverride = true;
  }
  if (options.paddingBottom !== undefined) {
    style.paddingBottom = options.paddingBottom;
    hasOverride = true;
  }
  return hasOverride ? style : undefined;
}

function resolveHeaderPad(options: {
  useFramePadding: boolean;
  isExpanded: boolean;
  headerPaddingHorizontal?: number | undefined;
  headerPaddingVertical?: number | undefined;
  headerPaddingBottom?: number | undefined;
}): ViewStyle | undefined {
  const headerOverride = buildPadOverride({
    ...(options.headerPaddingHorizontal !== undefined
      ? { paddingHorizontal: options.headerPaddingHorizontal }
      : {}),
    ...(options.headerPaddingVertical !== undefined
      ? { paddingVertical: options.headerPaddingVertical }
      : {}),
    // Bottom gap under header only when expanded — collapsed matches top inset.
    ...(options.headerPaddingBottom !== undefined && options.isExpanded
      ? { paddingBottom: options.headerPaddingBottom }
      : {}),
  });
  /** Frame mode zeros variant paddings; explicit header overrides still apply (guest Figma). */
  if (options.useFramePadding) {
    return headerOverride ?? styles.headerFlush;
  }
  return headerOverride;
}

function resolveContentPad(options: {
  useFramePadding: boolean;
  contentPaddingHorizontal?: number | undefined;
  contentPaddingTop?: number | undefined;
  contentPaddingBottom?: number | undefined;
}): ViewStyle | undefined {
  const contentOverride = buildPadOverride({
    ...(options.contentPaddingHorizontal !== undefined
      ? { paddingHorizontal: options.contentPaddingHorizontal }
      : {}),
    ...(options.contentPaddingTop !== undefined
      ? { paddingTop: options.contentPaddingTop }
      : {}),
    ...(options.contentPaddingBottom !== undefined
      ? { paddingBottom: options.contentPaddingBottom }
      : {}),
  });
  if (options.useFramePadding) {
    return contentOverride ?? styles.contentFlush;
  }
  return contentOverride;
}

/**
 * Default path (no subtitle): Typography is a direct header child with flex:1 —
 * identical DOM/style shape to the original UnitAccordion used by unit details.
 * Subtitle path wraps title+subtitle so guest can opt in without changing defaults.
 */
function WebAccordionTitle({
  title,
  subtitle,
  titleVariant,
  isRTL,
}: Readonly<{
  title: string;
  subtitle?: string | undefined;
  titleVariant: TypographyVariant;
  isRTL: boolean;
}>) {
  if (subtitle) {
    return (
      <View style={styles.textStack}>
        <Typography
          variant={titleVariant}
          color="primary"
          style={isRTL ? styles.titleRTL : undefined}
        >
          {title}
        </Typography>
        <Typography
          variant="footnote"
          color="tertiary"
          style={isRTL ? styles.titleRTL : undefined}
        >
          {subtitle}
        </Typography>
      </View>
    );
  }

  return (
    <Typography
      variant={titleVariant}
      color="primary"
      style={[styles.title, isRTL && styles.titleRTL]}
    >
      {title}
    </Typography>
  );
}

/**
 * Shared web accordion for properties-web (About / Documents) and guest-web.
 * Mobile app continues to use `Accordion` from this same molecules folder.
 */
export function WebAccordion({
  icon: Icon,
  title,
  subtitle,
  children,
  variant = 'large',
  defaultExpanded = false,
  onToggle,
  titleVariant: titleVariantOverride,
  headerPaddingHorizontal,
  headerPaddingVertical,
  headerPaddingBottom,
  contentPaddingHorizontal,
  contentPaddingTop,
  contentPaddingBottom,
  headerGap = 12,
  padding,
  paddingTop,
  sectionGap = 16,
}: Readonly<WebAccordionProps>) {
  const colors = useUIColors();
  const { isRTL } = useI18nContext();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const isMobile = isSmallScreen();
  const layout = resolveLayout(variant, isMobile);
  const useFramePadding = padding !== undefined;

  const handleToggle = () => {
    const next = !isExpanded;
    setIsExpanded(next);
    onToggle?.(next);
  };

  const headerPad = resolveHeaderPad({
    useFramePadding,
    isExpanded,
    headerPaddingHorizontal,
    headerPaddingVertical,
    headerPaddingBottom,
  });
  const contentPad = resolveContentPad({
    useFramePadding,
    contentPaddingHorizontal,
    contentPaddingTop,
    contentPaddingBottom,
  });
  const resolvedTitleVariant = titleVariantOverride ?? layout.titleVariant;

  return (
    <View
      style={[
        styles.container,
        { backgroundColor: colors.backgroundLevel1 },
        useFramePadding && {
          padding,
          ...(paddingTop !== undefined ? { paddingTop } : {}),
          gap: isExpanded ? sectionGap : 0,
        },
      ]}
    >
      <TouchableOpacity
        style={[
          styles.header,
          useFramePadding ? null : layout.headerStyle,
          headerPad,
          headerGap !== 12 ? { gap: headerGap } : null,
        ]}
        onPress={handleToggle}
        activeOpacity={0.7}
        accessibilityRole="button"
      >
        <Icon size={24} color={colors.fontPrimary} />
        <WebAccordionTitle
          title={title}
          subtitle={subtitle}
          titleVariant={resolvedTitleVariant}
          isRTL={isRTL}
        />
        {isExpanded ? (
          <ChevronUpIcon size={24} color={colors.fontPrimary} />
        ) : (
          <ChevronDownIcon size={24} color={colors.fontPrimary} />
        )}
      </TouchableOpacity>

      {isExpanded && (
        <View
          style={[useFramePadding ? null : layout.contentStyle, contentPad]}
        >
          {children}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 12,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  headerLarge: {
    paddingVertical: 24,
    paddingHorizontal: 16,
  },
  headerCompact: {
    padding: 16,
  },
  headerMobile: {
    padding: 16,
  },
  /** Used when frame `padding` owns the inset — no extra header padding. */
  headerFlush: {
    padding: 0,
  },
  contentFlush: {
    padding: 0,
  },
  /** Original UnitAccordion title style — keep for default (no-subtitle) path. */
  title: {
    flex: 1,
  },
  titleRTL: {
    textAlign: 'right',
  },
  textStack: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  contentLarge: {
    paddingTop: 16,
    paddingHorizontal: 24,
    paddingBottom: 24,
  },
  contentCompact: {
    paddingTop: 8,
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  contentMobile: {
    paddingTop: 12,
    paddingHorizontal: 16,
    paddingBottom: 24,
  },
});

export default WebAccordion;
